// ============================================================================
// LIGA AJUPAM — SurveyQuestion (v6.7.0)
// ============================================================================
// Renderiza una pregunta individual de la encuesta según su question_type.
// Es controlado: el padre maneja el valor a través de onChange.
// ============================================================================

import React from 'react';
import { Star } from 'lucide-react';
import type { SurveyQuestion as SurveyQuestionT } from '../services/api/surveys';

// `extraText` (v6.11.128.02) acompaña a RADIO/CHECKBOX cuando la opción elegida
// figura en `options_requiring_text`. Es lo que evita otro «Otro» ilegible.
export type SurveyAnswerValue =
  | { type: 'RATING_1_5'; rating: number | null }
  | { type: 'TEXT'; text: string }
  | { type: 'RADIO'; option: string | null; extraText?: string }
  | { type: 'CHECKBOX'; options: string[]; extraText?: string };

/** Opciones elegidas que exigen una explicación por escrito. */
export const selectedOptionsRequiringText = (
  q: SurveyQuestionT,
  value: SurveyAnswerValue
): string[] => {
  const required = q.options_requiring_text;
  if (!required || required.length === 0) return [];
  if (value.type === 'RADIO') {
    return value.option && required.includes(value.option) ? [value.option] : [];
  }
  if (value.type === 'CHECKBOX') {
    return value.options.filter((o) => required.includes(o));
  }
  return [];
};

interface SurveyQuestionProps {
  question: SurveyQuestionT;
  value: SurveyAnswerValue;
  onChange: (value: SurveyAnswerValue) => void;
  showError?: boolean;
}

export const SurveyQuestion: React.FC<SurveyQuestionProps> = ({
  question,
  value,
  onChange,
  showError,
}) => {
  const optionsNeedingText = selectedOptionsRequiringText(question, value);
  const needsExtraText = optionsNeedingText.length > 0;

  return (
    <div className={`space-y-2 ${showError ? 'pl-3 border-l-2 border-red-400' : ''}`}>
      <label className="block text-sm font-bold text-text-main">
        {question.question_text}
        {question.is_required && <span className="text-red-500 ml-1">*</span>}
      </label>

      {question.question_type === 'RATING_1_5' && value.type === 'RATING_1_5' && (
        <div className="flex items-center gap-2">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => onChange({ type: 'RATING_1_5', rating: n })}
              className="transition-transform hover:scale-110"
              aria-label={`Rating ${n} de 5`}
            >
              <Star
                size={32}
                className={
                  (value.rating ?? 0) >= n
                    ? 'fill-yellow-400 text-yellow-400'
                    : 'fill-none text-gray-300'
                }
              />
            </button>
          ))}
          {value.rating !== null && (
            <span className="text-sm text-gray-500 ml-2">{value.rating} / 5</span>
          )}
        </div>
      )}

      {question.question_type === 'TEXT' && value.type === 'TEXT' && (
        <textarea
          value={value.text}
          onChange={(e) => onChange({ type: 'TEXT', text: e.target.value })}
          rows={3}
          maxLength={1000}
          placeholder="Escribí tu respuesta..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary text-sm"
        />
      )}

      {question.question_type === 'RADIO' && value.type === 'RADIO' && question.options && (
        <div className="space-y-1">
          {question.options.map((opt) => (
            <label
              key={opt}
              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                value.option === opt ? 'bg-primary/5 border border-primary/20' : 'border border-transparent'
              }`}
            >
              <input
                type="radio"
                name={question.id}
                checked={value.option === opt}
                onChange={() => onChange({ type: 'RADIO', option: opt, extraText: value.extraText })}
                className="text-primary"
              />
              <span className="text-sm">{opt}</span>
            </label>
          ))}
        </div>
      )}

      {question.question_type === 'CHECKBOX' && value.type === 'CHECKBOX' && question.options && (
        <div className="space-y-1">
          {question.options.map((opt) => {
            const checked = value.options.includes(opt);
            return (
              <label
                key={opt}
                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                  checked ? 'bg-primary/5 border border-primary/20' : 'border border-transparent'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = checked
                      ? value.options.filter((x) => x !== opt)
                      : [...value.options, opt];
                    onChange({ type: 'CHECKBOX', options: next, extraText: value.extraText });
                  }}
                  className="text-primary"
                />
                <span className="text-sm">{opt}</span>
              </label>
            );
          })}
        </div>
      )}

      {/* Explicación obligatoria cuando la opción elegida la exige (v6.11.128.02) */}
      {needsExtraText && (value.type === 'RADIO' || value.type === 'CHECKBOX') && (
        <div className="pl-3 border-l-2 border-primary/30 space-y-1">
          <label className="block text-xs font-medium text-text-muted">
            Contanos a qué te referís con «{optionsNeedingText.join('», «')}»
            <span className="text-red-500 ml-1">*</span>
          </label>
          <textarea
            value={value.extraText ?? ''}
            onChange={(e) =>
              onChange(
                value.type === 'RADIO'
                  ? { type: 'RADIO', option: value.option, extraText: e.target.value }
                  : { type: 'CHECKBOX', options: value.options, extraText: e.target.value }
              )
            }
            rows={2}
            maxLength={500}
            placeholder="Una línea alcanza. Sin esto no sabemos qué mejorar."
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary/40 focus:border-primary"
          />
        </div>
      )}

      {showError && (
        <p className="text-xs text-red-600">
          {needsExtraText && !(value as any).extraText?.trim()
            ? 'Falta que expliques la opción que marcaste.'
            : 'Esta respuesta es obligatoria.'}
        </p>
      )}
    </div>
  );
};

// Inicializa un valor vacío del tipo correcto según la pregunta.
export const initialAnswerForQuestion = (q: SurveyQuestionT): SurveyAnswerValue => {
  switch (q.question_type) {
    case 'RATING_1_5':
      return { type: 'RATING_1_5', rating: null };
    case 'TEXT':
      return { type: 'TEXT', text: '' };
    case 'RADIO':
      return { type: 'RADIO', option: null };
    case 'CHECKBOX':
      return { type: 'CHECKBOX', options: [] };
  }
};

// Convierte el valor a la estructura que espera el RPC user_submit_survey.
export const answerValueToPayload = (
  q: SurveyQuestionT,
  value: SurveyAnswerValue
): Record<string, any> => {
  const base: Record<string, any> = { question_id: q.id };
  if (value.type === 'RATING_1_5' && value.rating !== null) {
    base.rating = value.rating;
  } else if (value.type === 'TEXT' && value.text.trim() !== '') {
    base.text = value.text.trim();
  } else if (value.type === 'RADIO' && value.option !== null) {
    base.options = value.option;
  } else if (value.type === 'CHECKBOX' && value.options.length > 0) {
    base.options = value.options;
  }
  // El texto de «Otro» viaja en el mismo answer; el RPC lo guarda en
  // survey_answers.answer_text, junto a las opciones (v6.11.128.02).
  if (
    (value.type === 'RADIO' || value.type === 'CHECKBOX') &&
    selectedOptionsRequiringText(q, value).length > 0 &&
    value.extraText?.trim()
  ) {
    base.text = value.extraText.trim();
  }
  return base;
};

// Determina si una respuesta cumple el requisito 'required'.
export const isAnswerProvided = (value: SurveyAnswerValue): boolean => {
  switch (value.type) {
    case 'RATING_1_5':
      return value.rating !== null;
    case 'TEXT':
      return value.text.trim().length > 0;
    case 'RADIO':
      return value.option !== null;
    case 'CHECKBOX':
      return value.options.length > 0;
  }
};

/**
 * Validación completa de una respuesta: además de 'required', exige el texto
 * de las opciones que lo piden. Se valida acá y también en el RPC — el front
 * para dar el mensaje en el momento, el server porque es quien manda.
 */
export const isAnswerComplete = (
  q: SurveyQuestionT,
  value: SurveyAnswerValue
): boolean => {
  if (q.is_required && !isAnswerProvided(value)) return false;
  if (selectedOptionsRequiringText(q, value).length > 0) {
    const extra = (value as { extraText?: string }).extraText;
    if (!extra || extra.trim().length === 0) return false;
  }
  return true;
};
